/**
 * The build as the thread shows it (R6, R55; board O „Nie udało się zbudować", „Szkic gotowy"): what
 * could not be built, and the finished draft as the program card, Buddy's summary and the
 * „Założyłem" box. Its progress is the message's own (`BuddyMessage`), as for every turn.
 */

import clsx from 'clsx';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { AiBuildResult, DraftVersion } from '~/entities/ai-conversation';
import { useScreenMotionPreference } from '~/shared/design-system/motion';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { FACT_SEPARATOR, schedulingWords } from '../lib/planWording';

import { Assumptions, type AssumedPicks } from './Assumptions';
import { ChevronGlyph, ErrorGlyph, ProgramGlyph } from './ChatGlyphs';
import { DraftFeedback } from './DraftFeedback';
import { ModelText } from './Messages';
import { RollingNumber } from './RollingNumber';
import surfaces from './surfaces.module.css';
import styles from './BuildReport.module.css';

interface FailureNoticeProps {
  /** Buddy's sentence naming what could not be built; `null` when the turn failed without one. */
  readonly failure: string | null;
  /** Buddy was switched off while the work waited. */
  readonly unavailable?: boolean;
  /** Buddy ran out of steps before it finished (`ai_step_limit`). */
  readonly stepLimit?: boolean;
  readonly retryable: boolean;
  readonly onRetry: () => void;
  /** „Wróć do zarysu", offered on a build that followed an outline. */
  readonly onBackToOutline: (() => void) | null;
}

/** Nothing reached the builder (R6): what failed, and what the trainer can do about it. */
export function FailureNotice({ failure, unavailable = false, stepLimit = false, retryable, onRetry, onBackToOutline }: FailureNoticeProps) {
  const { t } = useTranslation();
  return (
    <>
      <p className={surfaces.notice} role="alert">
        <ErrorGlyph className={clsx(surfaces.noticeGlyph, styles.bad)} />
        <span>
          {failure === null ? (
            t(unavailable ? 'web:aiPlan.failed.unavailable' : stepLimit ? 'web:aiPlan.failed.stepLimit' : 'web:aiPlan.failed.generic')
          ) : (
            <>
              {/* Buddy's words are a plain text child, never interpolated into markup (R33). */}
              <b>{t('web:aiPlan.failed.notBuiltTitle')}</b> {failure}
            </>
          )}
        </span>
      </p>
      {retryable && (
        <div className={styles.acts}>
          <button type="button" className={clsx(surfaces.button, surfaces.small)} onClick={onRetry}>
            {t('web:aiPlan.failed.retry')}
          </button>
          {onBackToOutline !== null && (
            <button type="button" className={clsx(surfaces.button, surfaces.small, surfaces.ghost)} onClick={onBackToOutline}>
              {t('web:aiPlan.failed.backToOutline')}
            </button>
          )}
        </div>
      )}
    </>
  );
}

/**
 * One session of the draft as the program card lists it (boards Q, R). The page reads these off
 * the plan document, the same projection the session's panel counts from, so the row and the panel
 * it opens never disagree.
 */
export interface DraftSessionRow {
  /** The day's slot token: what the panel opens on. */
  readonly daySlotToken: string;
  /** The letter the session wears on the card and in its panel's step list. */
  readonly letter: string;
  /** The session's own name; `null` when it has none. */
  readonly name: string | null;
  readonly exercises: number;
  /** Sets that count: every type but the warm-up. */
  readonly sets: number;
  /** The session's authored length; `null` when the draft states none (nothing estimates one). */
  readonly minutes: number | null;
  /** Names in this session still missing from the catalogue and undecided. */
  readonly missing: number;
}

/** The sessions of one phase; `phase` is `null` for a plan of one phase, and the list wears no label. */
export interface DraftSessionGroup {
  readonly phase: { readonly name: string; readonly fromCycle: number; readonly toCycle: number } | null;
  readonly rows: readonly DraftSessionRow[];
}

interface ProgramCardProps {
  readonly result: AiBuildResult;
  /** The draft's sessions, by phase; empty while the plan document cannot be read. */
  readonly sessions: readonly DraftSessionGroup[];
  /** The session whose panel is open beside the conversation, by its day's slot token. */
  readonly openSessionToken: string | null;
  readonly onOpenSession: (daySlotToken: string) => void;
  /** The session row owed focus: the one whose panel just closed. */
  readonly returnFocusToken: string | null;
  /** The draft landed while the thread was on screen: its top is brought into view, once. */
  readonly arrived: boolean;
  /** Which of the conversation's drafts this is (R11); every one of them opens. */
  readonly version: DraftVersion;
  /** How many unresolved exercises still wait for a decision, a reopened one included. */
  readonly undecided: number;
  /** Opens the draft in the builder. Refused, and saying why, while a name is undecided. */
  readonly onOpen: () => void;
  readonly onRefine: () => void;
  /** The cards for the exercises outside the catalogue, drawn under this one. */
  readonly unresolved: ReactNode;
  /** Buddy's own exercise picks (R8), listed in „Założyłem", each with its „Zmień". */
  readonly picks: AssumedPicks;
  /** The cards those „Zmień" opened, drawn right under „Założyłem". */
  readonly changes: ReactNode;
  /** Another version of this draft (R11); `null` when this one cannot be built again now. */
  readonly onRebuild: (() => void) | null;
  /** The trainer's rating (R47), and the one act that sets it, with a comment or without. */
  readonly feedback: boolean | null;
  readonly onRate: (helpful: boolean, comment: string | null) => Promise<boolean>;
  /** The spreadsheet an imported draft came from (board T); `null` for one Buddy built. */
  readonly importedFrom: string | null;
}

/** The finished draft: the program card, the unresolved exercises, the summary and the assumptions. */
export function BuiltDraft({
  result,
  sessions,
  openSessionToken,
  onOpenSession,
  returnFocusToken,
  arrived,
  version,
  undecided,
  onOpen,
  onRefine,
  unresolved,
  picks,
  changes,
  onRebuild,
  feedback,
  onRate,
  importedFrom,
}: ProgramCardProps) {
  const { t } = useTranslation();
  const reason = useId();
  const reducedMotion = useScreenMotionPreference();
  const top = useRef<HTMLDivElement>(null);
  const rows = useRef(new Map<string, HTMLButtonElement>());

  // A draft that lands under the trainer's eyes is brought into view by its top, once: the card
  // mounts with its result, so its mount is the arrival.
  useEffect(() => {
    if (arrived) top.current?.scrollIntoView({ block: 'start', behavior: reducedMotion ? 'auto' : 'smooth' });
  }, [arrived, reducedMotion]);

  // The panel closed: its row takes focus back, so Escape, the ✕ and Back leave the trainer where
  // they opened it.
  useEffect(() => {
    if (returnFocusToken !== null) rows.current.get(returnFocusToken)?.focus();
  }, [returnFocusToken]);
  const refusal = undecided > 0 ? t('web:aiPlan.program.decideFirst', { count: undecided }) : null;
  const facts = [
    t('web:aiPlan.program.eyebrow'),
    t('web:aiPlan.cycles', { count: result.cycleCount }),
    ...schedulingWords(t, result.schedulingMode, result.startWeekday),
  ];

  return (
    <>
      {version !== 'only' && <p className={styles.version}>{t(`web:aiPlan.program.version.${version}`)}</p>}
      <div ref={top} className={clsx(surfaces.card, surfaces.riseCard, styles.program)}>
        <p className={styles.programEyebrow}>
          <ProgramGlyph className={styles.programGlyph} />
          {facts.join(FACT_SEPARATOR)}
        </p>
        <h3 className={styles.programTitle}>{result.title}</h3>
        {importedFrom !== null && <p className={styles.origin}>{t('web:aiPlan.import.origin', { name: importedFrom })}</p>}
        <dl className={styles.stats}>
          <div className={styles.stat}>
            <dt>{t('web:aiPlan.program.sessionsPerCycle', { count: result.sessionsPerCycle })}</dt>
            <dd>{result.sessionsPerCycle}</dd>
          </div>
          <div className={styles.stat}>
            <dt>{t('web:aiPlan.program.fromCatalogue', { count: result.catalogueExercises })}</dt>
            <dd>
              <RollingNumber value={result.catalogueExercises} />
            </dd>
          </div>
          <div className={styles.stat}>
            <dt>{t('web:aiPlan.program.toDecide')}</dt>
            <dd>
              <RollingNumber value={undecided} />
            </dd>
          </div>
        </dl>
        {sessions.length > 0 && (
          <div className={styles.sessionGroups} role="group" aria-label={t('web:aiPlan.program.sessionsLabel')}>
            {sessions.map((group) => {
              // „Budowanie · cykle 5–8": the phase and the cycles it runs, a quiet line over its list.
              const label =
                group.phase === null
                  ? null
                  : [
                      group.phase.name,
                      group.phase.fromCycle === group.phase.toCycle
                        ? t('web:aiPlan.program.phaseCycle', { cycle: group.phase.fromCycle })
                        : t('web:aiPlan.program.phaseCycles', { from: group.phase.fromCycle, to: group.phase.toCycle }),
                    ]
                      .filter((part) => part.trim() !== '')
                      .join(FACT_SEPARATOR);
              return (
                <div key={group.phase?.fromCycle ?? 0} className={styles.sessionGroup}>
                  {label !== null && <p className={styles.sessionPhase}>{label}</p>}
                  <ol className={styles.sessions} aria-label={label ?? t('web:aiPlan.program.sessionsLabel')}>
                    {group.rows.map((session) => {
                      const open = session.daySlotToken === openSessionToken;
                      const facts = [
                        t('web:planBuilder.exercises', { count: session.exercises }),
                        t('web:planBuilder.sets', { count: session.sets }),
                        ...(session.missing > 0 ? [t('web:aiPlan.program.sessionMissing', { count: session.missing })] : []),
                      ];
                      return (
                        <li key={session.daySlotToken} className={styles.sessionItem}>
                          <button
                            type="button"
                            ref={(node) => {
                              if (node === null) rows.current.delete(session.daySlotToken);
                              else rows.current.set(session.daySlotToken, node);
                            }}
                            className={clsx(styles.session, open && styles.sessionOpen)}
                            aria-expanded={open}
                            onClick={() => {
                              onOpenSession(session.daySlotToken);
                            }}
                          >
                            <span className={styles.sessionLetter} aria-hidden="true">
                              {session.letter}
                            </span>
                            <span className={styles.sessionText}>
                              <span className={styles.sessionName}>{session.name ?? t('web:planBuilder.untitledSession')}</span>
                              <span className={styles.sessionFacts}>{facts.join(FACT_SEPARATOR)}</span>
                            </span>
                            {session.minutes !== null && (
                              <span className={styles.sessionMinutes}>
                                {t('web:aiPlan.program.sessionMinutes', { minutes: session.minutes })}
                              </span>
                            )}
                            <ChevronGlyph className={styles.sessionChevron} />
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              );
            })}
          </div>
        )}
        <p className={styles.programNote}>
          {sessions.length > 0 ? t('web:aiPlan.program.sessionsHint') : t('web:aiPlan.program.unsaved')}
        </p>
        <div className={styles.programActions}>
          <button
            type="button"
            className={clsx(surfaces.button, surfaces.primary)}
            aria-disabled={refusal === null ? undefined : 'true'}
            aria-describedby={refusal === null ? undefined : reason}
            onClick={() => {
              if (refusal === null) onOpen();
            }}
          >
            {t('web:aiPlan.program.open')}
          </button>
          {/* An earlier version only opens: what is refined or built again is the newest. */}
          {version !== 'earlier' && (
            <button type="button" className={clsx(surfaces.button, surfaces.ghost)} onClick={onRefine}>
              {t('web:aiPlan.program.refine')}
            </button>
          )}
          {onRebuild !== null && (
            <button type="button" className={clsx(surfaces.button, surfaces.ghost)} onClick={onRebuild}>
              {t('web:aiPlan.program.rebuild')}
            </button>
          )}
          <span id={reason} className={hidden.srOnly}>
            {refusal ?? ''}
          </span>
        </div>
      </div>
      {unresolved}
      <ModelText text={result.summary} />
      {result.warnings.length > 0 && (
        <ul className={styles.warnings} aria-label={t('web:aiPlan.warnings.label')}>
          {/* Steps marked for the trainer to check (R22); never a block. Plain text (R33). */}
          {result.warnings.map((warning, index) => (
            <li key={index} className={styles.warning}>
              {t('web:aiPlan.warnings.line', { exercise: warning.exerciseName, message: warning.message })}
            </li>
          ))}
        </ul>
      )}
      <Assumptions items={result.assumptions} foot={t('web:aiPlan.assumptions.foot')} picks={picks} />
      {changes}
      <DraftFeedback feedback={feedback} onRate={onRate} subject="draft" />
    </>
  );
}
